import { useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { useBreakpoint, useClickOutside } from 'hooks';
import { Modal } from 'components/modals';

export type DropdownProps = {
  children: React.ReactNode;
  items: (close: () => void) => React.ReactNode; // pass close functionality to children
  className?: string;
  top?: boolean;
  right?: boolean;
  left?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
};

export default function Dropdown({
  children,
  items,
  className,
  top,
  right,
  left,
  disabled,
  ariaLabel,
}: DropdownProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const { isBreakpointTo } = useBreakpoint();
  const smallDevice = isBreakpointTo('sm');

  const handleClick = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  const handleClickOutside = useCallback(() => {
    if (!smallDevice) {
      setOpen(false);
    }
  }, [smallDevice]);

  useClickOutside(ref, handleClickOutside);

  return (
    <div ref={ref} className="relative inline-block leading-none">
      {/* Dropdown toggle button */}
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={handleClick}
        className={classNames(
          { 'pointer-events-none opacity-30': disabled },
          className
        )}
        disabled={disabled}
      >
        {children}
      </button>

      {/* Dropdown menu */}
      {open &&
        (smallDevice ? (
          <Modal isOpen={open} onRequestClose={close}>
            {items(close)}
          </Modal>
        ) : (
          <div
            className={classNames(
              {
                'hidden': !open,
                '-translate-y-full': top,
                'translate-x-full': right,
                'right-0': left,
              },
              'w-max max-w-sm absolute transform mt-2 z-20 bg-white rounded-md shadow-2xl overflow-hidden'
            )}
          >
            {items(close)}
          </div>
        ))}
    </div>
  );
}
